import { PageHeader } from '../../components/office';
import { useState, useEffect, useMemo } from 'react';
import { staffApi, shiftsApi, financialConfigApi } from '@verlo/shared/services/api';
import { labourUplift, onCostWords } from '../../lib/labourCost';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { useAuthoringSite } from '../../contexts/SiteContext';
import Person from '../../components/people/Person';
import WeekPicker from '../../components/people/WeekPicker';
import { daysOf, mondayOf, ymd } from '../../lib/weeks';
import { TimeField } from '@verlo/shared/components/ui/time-field';
import { Notice } from '@verlo/shared/components/ui/notice';

// The rota (Back office sweep, Oct 2026). The days along the top and the people down the
// side stay put while the grid scrolls, as Bookings does on the till; a shift is its times on
// one line, so a row is one line high; the week calendar jumps to any week (D6).

const defaultShiftTemplates = [
  { id: 'morning', name: 'Morning', start_time: '06:00', end_time: '14:00' },
  { id: 'day', name: 'Day', start_time: '09:00', end_time: '17:00' },
  { id: 'lunch', name: 'Lunch', start_time: '11:00', end_time: '16:00' },
  { id: 'evening', name: 'Evening', start_time: '17:00', end_time: '23:00' },
  { id: 'split_am', name: 'Split AM', start_time: '11:00', end_time: '15:00' },
  { id: 'split_pm', name: 'Split PM', start_time: '18:00', end_time: '22:00' },
  { id: 'night', name: 'Night', start_time: '22:00', end_time: '06:00' },
];

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const TEMPLATES_KEY = 'rotaShiftTemplates';
const readTemplates = () => { try { return JSON.parse(localStorage.getItem(TEMPLATES_KEY) || '[]'); } catch { return []; } };

const RotaBuilderPage = () => {
  // Which venue a new row belongs to — see `useAuthoringSite`.
  const authoring = useAuthoringSite();
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [firstRota, setFirstRota] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [customTemplates, setCustomTemplates] = useState(readTemplates);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [formData, setFormData] = useState({ staff_id: '', date: '', start_time: '09:00', end_time: '17:00', role: '', notes: '' });
  // Wages plus on-costs, as Labour costs and the P&L count them, each venue at its own rate.
  // Someone who can't see the settings sees wages alone, and the summary doesn't claim
  // otherwise.
  const [finSettings, setFinSettings] = useState(null);

  const days = useMemo(() => daysOf(weekStart), [weekStart]);
  const today = ymd(new Date());
  const templates = [...defaultShiftTemplates, ...customTemplates];

  useEffect(() => {
    financialConfigApi.getSettings(authoring.site || undefined).then((r) => setFinSettings(r.data)).catch(() => setFinSettings(null));
  }, [authoring.site]);

  useEffect(() => {
    shiftsApi.firstDate().then((r) => r.data?.date && setFirstRota(new Date(`${r.data.date}T00:00:00`))).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, shiftsRes] = await Promise.all([staffApi.getAll(true), shiftsApi.getAll(ymd(days[0]), ymd(days[6]))]);
      setStaff(staffRes.data);
      setShifts(shiftsRes.data);
    } catch {
      toast.error("Couldn't load the rota");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreateDialog = (date, member = null) => {
    setEditingShift(null);
    setFormData({ staff_id: member?.id || '', date: ymd(date), start_time: '09:00', end_time: '17:00', role: member?.role || '', notes: '' });
    setDialogOpen(true);
  };
  const openEditDialog = (shift) => {
    setEditingShift(shift);
    setFormData({ staff_id: shift.staff_id, date: shift.date, start_time: shift.start_time, end_time: shift.end_time, role: shift.role, notes: shift.notes || '' });
    setDialogOpen(true);
  };

  const saveCurrentAsTemplate = () => {
    const name = newTemplateName.trim();
    if (!name) return;
    const updated = [...customTemplates, { id: `custom_${Date.now()}`, name, start_time: formData.start_time, end_time: formData.end_time }];
    setCustomTemplates(updated);
    try { localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated)); } catch { /* storage off */ }
    setNewTemplateName('');
    toast.success(`${name} saved as a template`);
  };
  const deleteCustomTemplate = (id) => {
    const updated = customTemplates.filter((t) => t.id !== id);
    setCustomTemplates(updated);
    try { localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated)); } catch { /* storage off */ }
  };

  const handleSubmit = async () => {
    if (!formData.staff_id || !formData.date) { toast.error('Pick who and which day'); return; }
    try {
      if (editingShift) await shiftsApi.update(editingShift.id, formData);
      else await shiftsApi.create(formData, authoring.site);
      toast.success(editingShift ? 'Shift saved' : 'Shift added');
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Couldn't save the shift");
    }
  };

  const deleteShift = async (shift) => {
    try {
      await shiftsApi.delete(shift.id);
      toast.success('Shift removed');
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Couldn't remove the shift");
    }
  };

  const copyPreviousWeek = async () => {
    const prev = daysOf(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() - 7));
    try {
      const prevShifts = (await shiftsApi.getAll(ymd(prev[0]), ymd(prev[6]))).data;
      for (const shift of prevShifts) {
        const d = new Date(`${shift.date}T00:00:00`);
        d.setDate(d.getDate() + 7);
        await shiftsApi.create({ ...shift, date: ymd(d) }, authoring.site);
      }
      toast.success(`Last week's ${prevShifts.length} shifts copied`);
      fetchData();
    } catch {
      toast.error("Couldn't copy last week");
    }
  };

  const shiftsFor = (date, staffId) => shifts.filter((s) => s.date === ymd(date) && s.staff_id === staffId);
  const hoursFor = (staffId) => shifts.filter((s) => s.staff_id === staffId).reduce((sum, s) => sum + (s.hours || 0), 0);
  const rateOf = (staffId) => staff.find((s) => s.id === staffId)?.hourly_rate || 0;
  const weekHours = shifts.reduce((sum, s) => sum + (s.hours || 0), 0);
  // Per shift, at its own venue's rate: "All sites" is the header's default, and every
  // venue's shifts come back together, so one rate for the lot could not agree with the
  // server (which costs each shift at its site's).
  const upliftFor = (s) => labourUplift(finSettings, s?.site_id || authoring.site || undefined);
  const costOf = (rows) => rows.reduce((sum, s) => sum + (s.hours || 0) * rateOf(s.staff_id) * upliftFor(s), 0);
  const weekCost = costOf(shifts);
  const upliftsUsed = [...new Set(shifts.map(upliftFor))];

  return (
    <div className="space-y-5" data-testid="rota-builder-page">
      <PageHeader
        title="Rota"
        purpose="Who works when, and what the week costs."
        actions={<Button variant="outline" onClick={copyPreviousWeek} data-testid="rota-copy-week">Copy last week</Button>}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <WeekPicker weekStart={weekStart} onChange={setWeekStart} earliest={firstRota} testId="rota-week" />
        <p className="text-[13px] text-ink-soft tabular-nums" data-testid="rota-week-summary">
          {shifts.length} shifts · {weekHours.toFixed(1)} hours · {money(weekCost)}{onCostWords(upliftsUsed) && ` ${onCostWords(upliftsUsed)}`}
        </p>
      </div>

      {/* An empty week says how to start it (Chris's review L15): the + in each cell only
          showed on hover, so a blank grid gave no way in. */}
      {!loading && shifts.length === 0 && staff.length > 0 && (
        <Notice tone="info" word="Rota" data-testid="rota-empty-week">
          Nothing on this week yet. Choose a + in anyone&apos;s day to add a shift, or Copy last week if it&apos;s the same.
        </Notice>
      )}

      {loading ? <Skeleton className="h-[480px]" /> : (
        <div className="max-h-[calc(100vh-240px)] overflow-auto border border-hairline bg-white" data-testid="rota-grid">
          <table className="w-full min-w-[980px] border-separate border-spacing-0 text-[13px]">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 w-[230px] border-b border-r border-hairline bg-surface-2 px-3 py-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Person</th>
                {days.map((d) => {
                  const isToday = ymd(d) === today;
                  return (
                    <th key={ymd(d)} className={`sticky top-0 z-20 border-b border-r border-hairline px-2 py-2 text-center ${isToday ? 'bg-verlo-tint' : 'bg-surface-2'}`}>
                      <div className="font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">{d.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                      <div className={`text-[14px] font-semibold ${isToday ? 'text-verlo' : 'text-ink'}`}>{d.getDate()}</div>
                    </th>
                  );
                })}
                <th className="sticky top-0 z-20 w-[110px] border-b border-hairline bg-surface-2 px-3 py-2 text-right font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Week</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((member) => (
                <tr key={member.id} className="group" data-testid={`rota-row-${member.id}`}>
                  <td className="sticky left-0 z-10 border-b border-r border-hairline bg-white px-3 py-2 group-hover:bg-hover-tint">
                    <Person name={member.name} role={member.role} size="sm" />
                  </td>
                  {days.map((d) => {
                    const dayShifts = shiftsFor(d, member.id);
                    const isToday = ymd(d) === today;
                    return (
                      <td key={ymd(d)} className={`border-b border-r border-hairline px-1.5 py-1.5 align-middle ${isToday ? 'bg-verlo-tint/40' : ''} group-hover:bg-hover-tint`}>
                        <div className="flex flex-col gap-1">
                          {dayShifts.map((shift) => (
                            <button key={shift.id} type="button" onClick={() => openEditDialog(shift)}
                              className="w-full whitespace-nowrap rounded border border-hairline bg-white px-1.5 py-1 text-center font-mono text-[12.5px] tabular-nums text-ink hover:border-verlo"
                              title={`${shift.hours}h${shift.notes ? ` · ${shift.notes}` : ''}`} data-testid={`rota-shift-${shift.id}`}>
                              {shift.start_time}–{shift.end_time}
                            </button>
                          ))}
                          {!dayShifts.length && (
                            <button type="button" onClick={() => openCreateDialog(d, member)} aria-label={`Add a shift for ${member.name} on ${d.toLocaleDateString('en-GB', { weekday: 'long' })}`}
                              className="grid h-7 w-full place-items-center rounded text-ink-faint opacity-30 hover:bg-white hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-60">
                              <Plus size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="border-b border-hairline px-3 py-2 text-right tabular-nums">
                    <div className="font-mono text-ink">{hoursFor(member.id).toFixed(1)}h</div>
                    <div className="font-mono text-[12px] text-ink-faint">{money(costOf(shifts.filter((s) => s.staff_id === member.id)))}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingShift ? 'Edit the shift' : 'Add a shift'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              {/* Each label names its field (Chris's review L16). */}
              <Label htmlFor="shift-who">Who</Label>
              <Select value={formData.staff_id} onValueChange={(val) => setFormData({ ...formData, staff_id: val, role: staff.find((s) => s.id === val)?.role || '' })}>
                <SelectTrigger id="shift-who"><SelectValue placeholder="Pick someone" /></SelectTrigger>
                <SelectContent>{staff.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shift-day">Day</Label>
              <Input id="shift-day" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
            </div>
            <div className="space-y-2" role="group" aria-labelledby="shift-times">
              <Label id="shift-times">Times</Label>
              <div className="flex flex-wrap gap-1.5">
                {templates.map((t) => (
                  <span key={t.id} className="inline-flex items-center">
                    <button type="button" onClick={() => setFormData({ ...formData, start_time: t.start_time, end_time: t.end_time })}
                      className={`rounded-full border px-2.5 py-1 text-[12.5px] hover:bg-hover-tint ${formData.start_time === t.start_time && formData.end_time === t.end_time ? 'border-verlo bg-verlo-tint text-verlo' : 'border-hairline-strong text-ink'}`}
                      data-testid={`template-${t.id}`}>
                      {t.name} <span className="font-mono text-ink-faint">{t.start_time}–{t.end_time}</span>
                    </button>
                    {t.id.startsWith('custom_') && (
                      <button type="button" onClick={() => deleteCustomTemplate(t.id)} className="ml-0.5 p-1 text-ink-faint hover:text-signal-stop" aria-label={`Remove the ${t.name} template`}><Trash2 size={12} /></button>
                    )}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TimeField value={formData.start_time} onChange={(t) => setFormData({ ...formData, start_time: t })} aria-label="Starts" className="w-full" />
                <TimeField value={formData.end_time} onChange={(t) => setFormData({ ...formData, end_time: t })} aria-label="Ends" className="w-full" />
              </div>
              {!editingShift && (
                <div className="flex items-center gap-2">
                  <Input placeholder="Save these times as…" aria-label="Name for these times" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} className="flex-1" />
                  <Button variant="outline" size="sm" onClick={saveCurrentAsTemplate} disabled={!newTemplateName.trim()}>Save</Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="shift-note">Note</Label>
              <Input id="shift-note" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="e.g. covering for Amy" />
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <div>
              {editingShift && (
                <Button variant="ghost" className="text-signal-stop hover:text-signal-stop" onClick={() => deleteShift(editingShift)}>Remove the shift</Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button className="bg-verlo hover:bg-verlo-hover" onClick={handleSubmit}>{editingShift ? 'Save' : 'Add the shift'}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RotaBuilderPage;
